import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Paperclip, X, File, Image, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const getFileIcon = (type: string) => {
  if (type.startsWith("image/")) return <Image className="h-4 w-4" />;
  if (type === "application/pdf") return <FileText className="h-4 w-4" />;
  return <File className="h-4 w-4" />;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export interface MessageAttachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

interface MessageAttachmentUploadProps {
  onAttachmentsChange: (attachments: MessageAttachment[]) => void;
  attachments: MessageAttachment[];
  disabled?: boolean;
}

export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
export const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

export const uploadFile = async (file: File): Promise<MessageAttachment | null> => {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) {
      toast.error("You must be logged in to upload files");
      return null;
    }

    const userId = session.session.user.id;
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const filePath = `${userId}/${timestamp}-${sanitizedName}`;

    const { error: uploadError } = await supabase.storage
      .from("message-attachments")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      toast.error(`Failed to upload ${file.name}`);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from("message-attachments")
      .getPublicUrl(filePath);

    return {
      id: `${timestamp}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      url: urlData.publicUrl,
      type: file.type,
      size: file.size,
    };
  } catch (error) {
    console.error("Upload error:", error);
    toast.error(`Failed to upload ${file.name}`);
    return null;
  }
};

export const MessageAttachmentUpload = ({
  onAttachmentsChange,
  attachments,
  disabled,
}: MessageAttachmentUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Use the exported uploadFile function

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled) return;

      // Filter valid files
      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} is too large. Max size is 20MB.`);
          return false;
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          toast.error(`${file.name} has an unsupported file type.`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      setIsUploading(true);
      setUploadProgress(0);

      const newAttachments: MessageAttachment[] = [];
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const attachment = await uploadFile(file);
        if (attachment) {
          newAttachments.push(attachment);
        }
        setUploadProgress(((i + 1) / validFiles.length) * 100);
      }

      if (newAttachments.length > 0) {
        onAttachmentsChange([...attachments, ...newAttachments]);
        toast.success(
          `Uploaded ${newAttachments.length} file${newAttachments.length > 1 ? "s" : ""}`
        );
      }

      setIsUploading(false);
      setUploadProgress(0);
    },
    [attachments, onAttachmentsChange, disabled]
  );

  const removeAttachment = (id: string) => {
    onAttachmentsChange(attachments.filter((a) => a.id !== id));
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled,
  });

  return (
    <div {...getRootProps()} className="relative">
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10">
          <p className="text-sm font-medium text-primary">Drop files here...</p>
        </div>
      )}

      {/* Upload button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={open}
        disabled={disabled || isUploading}
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Paperclip className="h-4 w-4" />
        )}
      </Button>

      {/* Upload progress */}
      {isUploading && (
        <div className="absolute bottom-full left-0 right-0 mb-2">
          <Progress value={uploadProgress} className="h-1" />
        </div>
      )}
    </div>
  );
};

export const AttachmentPreviewBar = ({
  attachments,
  onRemove,
}: {
  attachments: MessageAttachment[];
  onRemove: (id: string) => void;
}) => {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-lg mb-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className={cn(
            "flex items-center gap-2 px-2 py-1 rounded",
            "bg-background border text-sm"
          )}
        >
          {getFileIcon(attachment.type)}
          <span className="max-w-[120px] truncate">{attachment.name}</span>
          <span className="text-xs text-muted-foreground">
            {formatFileSize(attachment.size)}
          </span>
          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
};
